/**
 * Build and submit Sell Timber flow data to the auctions API (same backend as AdFlow / AuctionsContext).
 * Creates an Ad via POST /auctions-api/auctions/, uploads attachments, then one PATCH with
 * forestWithSections + custom_area + crs 4326 + state CREATED (20), then POSTs review=1 so the ad reaches
 * WAITING_FOR_REVIEW (30)
 * and skips the separate review page (same server path as “Skicka till granskning”).
 */

import { apiFetch } from '../../../../../api/client';
import {
  uploadAdFile,
  createAdAttachment,
  adApiRequest,
  submitAdForReviewById
} from '../../../../../api/services/ad';

import {
  buildGrotFinalizePatch,
  fellingApplicationKeyFromFeature,
  formatSellTimberDate,
  GROT_ARRANGEMENT,
  grotCommentsForApi,
  grotHasAtLeastOneAttachment,
  grotStep3ValidationMessages,
  isGrotArrangement,
  normalizeGrotJaNejForApi,
  roundHa,
  SELL_TIMBER_DEFAULT_SELECTED_SALE,
  SELL_TIMBER_EXTRA_SPECIES,
  SELL_TIMBER_GENERIC_ERROR,
  SELL_TIMBER_GROT_AD_TYPE
} from './sellTimberShared';


const AD_TYPES_URL = '/auctions-api/auctionstype/';
const ADS_LIST_URL = '/auctions-api/auctions/';

/**
 * @param {number} pct 0–100
 * @returns {string} fraction 0–1 as string for TGL HStore
 */
function pctToTglString(pct) {
  const n = Number(pct);
  if (Number.isNaN(n)) return '0';
  return String(n / 100);
}

function tglFractionToPctString(v) {
  if (v === '' || v == null) return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return String(Math.round(n * 100));
}

/** Matches auto names from {@link collectFeaturesFromMapSelection} / step 3 — renumbered for API, not sent verbatim. */
const RE_NAME_VALD_AVDELNING = /^Vald avdelning\s+/i;
const RE_NAME_RITAD_AVDELNING = /^Ritad avdelning\s*#\s*\d+/i;
const RE_NAME_ANNONSUTKAST = /^Annonsutkast\s*#\s*\d+/i;
/** Avverkningsanmälningar — keep full label (e.g. "Avverkning 12345"). */
const RE_NAME_AVVERKNING = /^Avverkning\s+/i;

function shouldRenumberSellTimberSectionNameForApi(name) {
  if (name == null || String(name).trim() === '') return false;
  const s = String(name).trim();
  if (RE_NAME_AVVERKNING.test(s)) return false;
  return (
    RE_NAME_VALD_AVDELNING.test(s) ||
    RE_NAME_RITAD_AVDELNING.test(s) ||
    RE_NAME_ANNONSUTKAST.test(s)
  );
}

/**
 * Wizard `forestWithSections` ({ sections }) -> API payload for AdSerializer (ForestSection + TGL).
 */
export function buildForestWithSectionsPayload(forestWithSections) {
  const rows = forestWithSections?.sections ?? [];
  let avdelningSeq = 0;
  const sections = rows.map((row) => {
    let nameForApi = row.name != null ? String(row.name) : '';
    if (shouldRenumberSellTimberSectionNameForApi(nameForApi)) {
      avdelningSeq += 1;
      nameForApi = `Avdelning ${avdelningSeq}`;
    }

    const vol = Number(row.volumeM3fub);
    const totalVolume = Number.isFinite(vol) ? Math.round(vol) : 0;
    const ha = row.area !== '' && row.area != null ? Number(row.area) : NaN;
    const Hectares = Number.isFinite(ha) ? ha.toFixed(3) : null;

    const TGL = {
      Tall: pctToTglString(row.tall),
      Gran: pctToTglString(row.gran),
      default: pctToTglString(row.lov)
    };
    for (const { key, label } of SELL_TIMBER_EXTRA_SPECIES) {
      if (row[key] === '' || row[key] == null || Number.isNaN(Number(row[key]))) continue;
      if (Number(row[key]) === 0) continue;
      TGL[label] = pctToTglString(row[key]);
    }

    return {
      Name: nameForApi,
      totalVolume,
      habitatIndex: null,
      Hectares,
      TGL
    };
  });
  return { sections };
}

/**
 * GeoJSON from GET /auctions/:id/ (FeatureCollection, GeometryCollection, etc.) — non-empty for map validation.
 */
export function isNonEmptyAdGeometry(geo) {
  if (!geo || typeof geo !== 'object') return false;
  if (geo.type === 'FeatureCollection') return (geo.features?.length ?? 0) > 0;
  if (geo.type === 'GeometryCollection') return (geo.geometries?.length ?? 0) > 0;
  if (geo.type === 'Feature' && geo.geometry) return true;
  return Boolean(geo.type && geo.coordinates);
}

/** Stable id for map-placed Point features (`properties.sellTimberMarkerId`). */
export function newSellTimberMarkerId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `st-m-${crypto.randomUUID()}`;
  }
  return `st-m-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** @returns {[number, number] | null} WGS84 lng/lat from a length-2 array or tuple. */
function toFiniteLngLat(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const lng = Number(coordinates[0]);
  const lat = Number(coordinates[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return [lng, lat];
}

/**
 * Return a GeoJSON object like `geo` but without Point geometries, so the Sell Timber map
 * can render Points only via `placedMarkers` (avoids duplicate L.geoJSON + marker layers).
 */
export function adGeometryOmittingPoints(geo) {
  if (!geo || typeof geo !== 'object') return null;
  if (geo.type === 'FeatureCollection' && Array.isArray(geo.features)) {
    const features = geo.features.filter((f) => f?.geometry && f.geometry.type !== 'Point');
    if (features.length === 0) return null;
    return { type: 'FeatureCollection', crs: geo.crs, features };
  }
  if (geo.type === 'GeometryCollection' && Array.isArray(geo.geometries)) {
    const geometries = geo.geometries.filter((g) => g && g.type !== 'Point');
    if (geometries.length === 0) return null;
    if (geometries.length === 1) return geometries[0];
    return { type: 'GeometryCollection', crs: geo.crs, geometries };
  }
  if (geo.type === 'Feature' && geo.geometry?.type === 'Point') return null;
  if (geo.type === 'Point') return null;
  return geo;
}

function collectPointCoordinateArraysFromGeo(geo) {
  if (!geo || typeof geo !== 'object') return [];
  if (geo.type === 'Point' && geo.coordinates) return [geo.coordinates];
  if (geo.type === 'FeatureCollection' && Array.isArray(geo.features)) {
    return geo.features
      .filter((f) => f?.geometry?.type === 'Point' && f.geometry.coordinates)
      .map((f) => f.geometry.coordinates);
  }
  if (geo.type === 'GeometryCollection' && Array.isArray(geo.geometries)) {
    return geo.geometries.filter((g) => g?.type === 'Point' && g.coordinates).map((g) => g.coordinates);
  }
  return [];
}

function buildPlacedMarkersFromPlacedMapRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const pair = toFiniteLngLat([row.lng, row.lat]);
      if (!pair) return null;
      return {
        type: 'Feature',
        properties: {
          sellTimberMarkerId: row.sellTimberMarkerId ?? newSellTimberMarkerId(),
          name: row.name != null ? String(row.name) : '',
          icon: row.icon
        },
        geometry: { type: 'Point', coordinates: pair }
      };
    })
    .filter(Boolean);
}

function extractPlacedMarkersFromCustomAreaOnly(geo) {
  return collectPointCoordinateArraysFromGeo(geo)
    .map((c) => {
      const pair = toFiniteLngLat(c);
      if (!pair) return null;
      return {
        type: 'Feature',
        properties: {
          sellTimberMarkerId: newSellTimberMarkerId(),
          name: 'Markör',
          icon: 'bestand'
        },
        geometry: { type: 'Point', coordinates: pair }
      };
    })
    .filter(Boolean);
}

/**
 * Point markers from `geometry.placedMarkers` (each has `properties.sellTimberMarkerId`).
 *
 * @param {object | null | undefined} geometry
 * @returns {Array<{ type: 'Feature', properties: object, geometry: object }>}
 */
export function coercePlacedMarkersArray(geometry) {
  if (!geometry || typeof geometry !== 'object') return [];

  const fromArray = geometry.placedMarkers;
  if (!Array.isArray(fromArray) || !fromArray.length) return [];

  return fromArray
    .filter((f) => f?.geometry?.type === 'Point')
    .map((f) => {
      const props = f.properties && typeof f.properties === 'object' ? f.properties : {};
      const id = props.sellTimberMarkerId ?? newSellTimberMarkerId();
      return {
        type: 'Feature',
        properties: { ...props, sellTimberMarkerId: id },
        geometry: f.geometry
      };
    });
}

/**
 * Stored wizard geometry: canonical `placedMarkers` only (strips accidental `placedMarker` if present).
 *
 * @param {object | null | undefined} geometry
 * @returns {object | null | undefined}
 */
export function normalizeGeometryForWizard(geometry) {
  if (geometry == null) return geometry;
  const placedMarkers = coercePlacedMarkersArray(geometry);
  const { placedMarker: _omit, ...rest } = geometry;
  return { ...rest, placedMarkers };
}

/**
 * Manual map picks only: sections, fellings, drawn polygons, placed markers (no `customAreaFromAd`).
 *
 * @param {object} geometry — wizard working state, not the PATCH `custom_area` blob alone
 * @returns {GeoJSON.Feature[]}
 */
function collectManualMapFeatures(geometry) {
  const features = [];

  const selectedSections = new Set((geometry.selectedSectionLocalIds ?? []).map(String));
  for (const sect of geometry.sections ?? []) {
    if (!selectedSections.has(String(sect.localID))) continue;
    if (sect.geom?.type && sect.geom.coordinates) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: sect.geom
      });
    }
  }

  const selectedFellings = new Set((geometry.selectedFellingApplicationIds ?? []).map(String));
  const fc = geometry.fellingApplicationsGeojson;
  const fellingFeatures =
    fc?.type === 'FeatureCollection' ? fc.features : fc?.type === 'Feature' ? [fc] : [];
  fellingFeatures.forEach((feature, index) => {
    if (!selectedFellings.has(fellingApplicationKeyFromFeature(feature, index))) return;
    if (feature?.geometry) {
      features.push({
        type: 'Feature',
        properties: feature.properties || {},
        geometry: feature.geometry
      });
    }
  });

  for (const poly of geometry.drawnPolygons ?? []) {
    if (poly?.type === 'Feature' && poly.geometry) {
      features.push(poly);
    } else if (poly?.geometry?.type === 'Polygon') {
      features.push({ type: 'Feature', properties: poly.properties || {}, geometry: poly.geometry });
    }
  }

  features.push(...coercePlacedMarkersArray(geometry));
  return features;
}

/**
 * User-built map selection only (no draft geometry fallback).
 */
function buildManualCustomAreaFromGeometry(geometry) {
  if (!geometry) return null;
  const features = collectManualMapFeatures(geometry);
  if (!features.length) return null;
  return { type: 'FeatureCollection', features };
}

/**
 * Normalize draft GeoJSON from the API to a list of Features for merging with manual picks / step 3 fetch.
 */
export function draftGeometryToFeatures(geo) {
  if (!geo || typeof geo !== 'object') return [];
  if (geo.type === 'FeatureCollection') {
    return (geo.features ?? []).filter((f) => f?.geometry);
  }
  if (geo.type === 'Feature' && geo.geometry) {
    return [geo];
  }
  if (geo.type === 'GeometryCollection' && Array.isArray(geo.geometries)) {
    return geo.geometries
      .filter((g) => g != null)
      .map((g) => ({ type: 'Feature', properties: {}, geometry: g }));
  }
  if (geo.type && geo.coordinates != null && String(geo.type).indexOf('Feature') === -1) {
    return [{ type: 'Feature', properties: {}, geometry: geo }];
  }
  return [];
}

function cloneFeaturesForPayload(features) {
  try {
    return features.map((f) => JSON.parse(JSON.stringify(f)));
  } catch {
    return [];
  }
}

/**
 * Wizard working `geometry` → merged GeoJSON for PATCH `custom_area` (+ `crs` elsewhere).
 * The API persists a PostGIS/GEOS `GeometryCollection`; GET `custom_area` is serialized geometry,
 * not a round-trip of this FeatureCollection. Wizard state keeps structured fields (`sections`,
 * `placedMarkers`, …); this function merges them for the wire format.
 */
export function buildCustomAreaFromGeometry(geometry) {
  if (!geometry) return null;
  const manual = buildManualCustomAreaFromGeometry(geometry);
  const ca = geometry.customAreaFromAd;
  const draftFeatures =
    isNonEmptyAdGeometry(ca) && ca ? draftGeometryToFeatures(ca) : [];

  if (manual && draftFeatures.length) {
    return {
      type: 'FeatureCollection',
      features: [...manual.features, ...cloneFeaturesForPayload(draftFeatures)]
    };
  }
  if (manual) return manual;
  if (draftFeatures.length) {
    return {
      type: 'FeatureCollection',
      features: cloneFeaturesForPayload(draftFeatures)
    };
  }
  if (isNonEmptyAdGeometry(ca)) {
    try {
      return JSON.parse(JSON.stringify(ca));
    } catch {
      return null;
    }
  }
  return null;
}

/** True when geometry step has usable map data (manual selection or hydrated ad geometry). */
export function hasMapGeometrySelection(geometry) {
  if (!geometry) return false;
  if (isNonEmptyAdGeometry(geometry.customAreaFromAd)) return true;
  return buildManualCustomAreaFromGeometry(geometry) != null;
}

function mapForestSectionToRow(sec, index) {
  const TGL = sec.TGL || {};
  /** Serializer `totalVolume` is m³fub (see {@link buildForestWithSectionsPayload}); step 3 validates `volumeM3sk`. */
  let volumeM3sk = '';
  let volumeM3fub = '';
  if (sec.totalVolume != null && sec.totalVolume !== '') {
    const fubRounded = Math.round(Number(sec.totalVolume));
    if (Number.isFinite(fubRounded)) {
      volumeM3fub = String(fubRounded);
      volumeM3sk = String(Math.round(fubRounded / 0.83));
    }
  }
  const row = {
    id: `#${index + 1}`,
    name: sec.Name != null ? String(sec.Name) : '',
    area: sec.Hectares != null && sec.Hectares !== '' ? String(roundHa(sec.Hectares)) : '',
    volumeM3sk,
    volumeM3fub,
    tall: tglFractionToPctString(TGL.Tall),
    gran: tglFractionToPctString(TGL.Gran),
    lov: tglFractionToPctString(TGL.Lov != null ? TGL.Lov : TGL.default),
    al: '',
    asp: '',
    björk: '',
    bok: '',
    ek: ''
  };
  const extras = [
    ['Al', 'al'],
    ['Asp', 'asp'],
    ['Björk', 'björk'],
    ['Bok', 'bok'],
    ['Ek', 'ek']
  ];
  for (const [tglKey, rowKey] of extras) {
    if (TGL[tglKey] != null && TGL[tglKey] !== '') {
      const p = tglFractionToPctString(TGL[tglKey]);
      if (p !== '' && p !== '0') row[rowKey] = p;
    }
  }
  return row;
}

function findOwnedPropertyByDesignation(designation) {
  const props = typeof Window !== 'undefined' ? Window.react_data?.owned_props || [] : [];
  const d = (designation || '').trim().toLowerCase();
  const hit = props.find((p) => (p.designation || '').trim().toLowerCase() === d);
  if (!hit) return null;
  return { ref: String(hit.ref), designation: String(hit.designation ?? '') };
}

/**
 * Maps GET /auctions-api/auctions/:id/ `attachments` (`{ id, name }`) to TwFileUpload `FileItem` rows.
 * `existing` marks server-side rows (no `File`); upload step skips them.
 */
function mapAdAttachmentsToFileItems(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments.map((a) => ({
    id: a.id,
    name: String(a.name ?? 'bilaga'),
    size: '—',
    valid: true,
    existing: true
  }));
}

/** GET /auctionstype/ row: `type` id → wizard `selectedSale` (`label`). */
function resolveSelectedSaleFromTypeId(adTypesPayload, typeId) {
  if (typeId == null || !Array.isArray(adTypesPayload)) return SELL_TIMBER_DEFAULT_SELECTED_SALE;
  const row = adTypesPayload.find((t) => t.id === typeId || String(t.id) === String(typeId));
  return row?.label ?? SELL_TIMBER_DEFAULT_SELECTED_SALE;
}

/** GET /auctionstype/ row: wizard `selectedSale` (`label`) → `type` id for POST/PATCH. */
function resolveAdTypeId(adTypesPayload, selectedSale) {
  if (!selectedSale || !Array.isArray(adTypesPayload)) return null;
  const row = adTypesPayload.find((t) => t.label === selectedSale);
  return row?.id ?? null;
}

/**
 * Load an existing ad (e.g. draft from "Skapa annons" / map-api/create-ad/) into Sell Timber step state.
 *
 * @returns {Promise<{ ok: true, adCreateData: object, geometry: object, forestWithSections: object, editingAdId: number } | { ok: false, message: string, status?: number, data?: unknown }>}
 */
export async function fetchAdForSellTimber(adId) {
  const [adRes, typesRes] = await Promise.all([adApiRequest('GET', adId), fetchAdTypes()]);
  if (!adRes.ok) {
    return submitFail(adRes);
  }
  if (!typesRes.ok) {
    return submitFail(typesRes);
  }

  const ad = adRes.data;
  const todayIso = new Date().toISOString().slice(0, 10);
  const selectedAction = ad.arrangement ?? 'slutavverkning';
  const selectedSale = isGrotArrangement(selectedAction)
    ? SELL_TIMBER_GROT_AD_TYPE
    : resolveSelectedSaleFromTypeId(typesRes.data, ad.type);
  const prop = findOwnedPropertyByDesignation(ad.description_of_property);

  const startYmd = formatSellTimberDate(ad.start_time, { style: 'ymd' }) || todayIso;
  const end_time = formatSellTimberDate(ad.end_time, { style: 'ymd' }) || '';

  const adCreateData = {
    selectedProperty: prop,
    selectedAction,
    selectedSale,
    end_time,
    comments: ad.comments != null ? String(ad.comments) : '',
    attachments: mapAdAttachmentsToFileItems(ad.attachments),
    start_time: startYmd,
    startTimeFormatted: formatSellTimberDate(startYmd, { style: 'svLong', emptyLabel: '' }),
    ...(isGrotArrangement(selectedAction)
      ? {
          grot_volume: ad.grot_volume != null ? String(ad.grot_volume) : '',
          grot_heap_covered: normalizeGrotJaNejForApi(ad.grot_heap_covered) ?? '',
          grot_collected: normalizeGrotJaNejForApi(ad.grot_collected) ?? '',
          grotCommentsExtra: String(ad.comments ?? '').trim()
        }
      : {})
  };

  const custom = ad.custom_area;
  let placedMarkerFeatures = buildPlacedMarkersFromPlacedMapRows(ad.placed_map_markers);
  if (!placedMarkerFeatures.length) {
    placedMarkerFeatures = extractPlacedMarkersFromCustomAreaOnly(custom);
  }
  const customStripped =
    placedMarkerFeatures.length > 0 ? adGeometryOmittingPoints(custom) : custom;
  const customForWizard =
    customStripped && isNonEmptyAdGeometry(customStripped)
      ? customStripped
      : custom && isNonEmptyAdGeometry(custom) && !placedMarkerFeatures.length
        ? custom
        : null;
  const geometry = {
    customAreaFromAd: customForWizard,
    ...(placedMarkerFeatures.length ? { placedMarkers: placedMarkerFeatures } : {})
  };

  const rawSections = ad.forestWithSections?.sections ?? [];
  const forestWithSections = {
    sections: rawSections.map(mapForestSectionToRow)
  };

  return {
    ok: true,
    adCreateData,
    geometry,
    forestWithSections,
    editingAdId: ad.id
  };
}

export async function fetchAdTypes() {
  return apiFetch(AD_TYPES_URL, { method: 'GET' });
}

async function uploadAttachmentsForAd(attachments, adId) {
  const list = attachments ?? [];
  for (const entry of list) {
    const file = entry?.file;
    if (!(file instanceof File)) continue;
    const fileFormData = new FormData();
    fileFormData.append('file', file);
    fileFormData.append('original_file_name', file.name);
    const uploadRes = await uploadAdFile(fileFormData);
    if (!uploadRes.ok) {
      return { ok: false, message: SELL_TIMBER_GENERIC_ERROR };
    }
    const attachRes = await createAdAttachment(uploadRes.data, adId);
    if (!attachRes.ok) {
      return { ok: false, message: SELL_TIMBER_GENERIC_ERROR };
    }
  }
  return { ok: true };
}

/** @param {unknown} adId — included on the result when not nullish (partial submit recovery). */
function submitFail(res, adId) {
  const out = {
    ok: false,
    message: SELL_TIMBER_GENERIC_ERROR,
    status: res.status,
    data: res.data
  };
  if (adId != null) out.adId = adId;
  return out;
}

/**
 * @param {object} adCreateData
 * @param {number} typeId
 * @param {string} arrangement
 * @param {string} comments
 */
function sellTimberCoreCreateFields(adCreateData, typeId, arrangement, comments) {
  const start_time = adCreateData?.start_time ?? null;
  const end_time = adCreateData?.end_time ?? null;
  if (!start_time || !end_time) {
    return { ok: false, message: 'Start- och slutdatum måste anges (steg 1).' };
  }
  const description_of_property = (adCreateData?.selectedProperty?.designation ?? '').trim();
  return {
    ok: true,
    createBody: {
      type: typeId,
      arrangement,
      description_of_property: description_of_property || null,
      comments: comments || null,
      start_time,
      end_time,
      show_direct_contact_info: true
    }
  };
}

async function resolveOrCreateAdId(createBody, existingAdId) {
  if (existingAdId != null) {
    const patchRes = await adApiRequest('PATCH', existingAdId, createBody);
    if (!patchRes.ok) {
      return submitFail(patchRes);
    }
    return { ok: true, adId: existingAdId };
  }
  const createRes = await apiFetch(ADS_LIST_URL, {
    method: 'POST',
    body: JSON.stringify(createBody)
  });
  if (!createRes.ok) {
    return submitFail(createRes);
  }
  const newId = createRes.data?.id;
  if (newId == null) {
    return { ok: false, message: SELL_TIMBER_GENERIC_ERROR };
  }
  return { ok: true, adId: newId };
}

/**
 * @param {{
 *   validate: () => { ok: true } | { ok: false, message: string },
 *   createBody: object,
 *   buildFinalizePatch: () => object,
 *   attachments: Array,
 *   existingAdId?: number
 * }} args
 */
async function submitSellTimberAdPipeline({
  validate,
  createBody,
  buildFinalizePatch,
  attachments,
  existingAdId
}) {
  const validation = validate();
  if (!validation.ok) {
    return validation;
  }

  const resolvedAd = await resolveOrCreateAdId(createBody, existingAdId);
  if (!resolvedAd.ok) {
    return resolvedAd;
  }
  const adId = resolvedAd.adId;

  const filesRes = await uploadAttachmentsForAd(attachments, adId);
  if (!filesRes.ok) {
    return { ok: false, message: SELL_TIMBER_GENERIC_ERROR, adId };
  }

  const finalizeRes = await adApiRequest('PATCH', adId, buildFinalizePatch());
  if (!finalizeRes.ok) {
    return submitFail(finalizeRes, adId);
  }

  const reviewRes = await submitAdForReviewById(adId);
  if (reviewRes.needsRedirect && reviewRes.redirectUrl) {
    return {
      ok: false,
      needsRedirect: true,
      redirectUrl: reviewRes.redirectUrl,
      adId
    };
  }
  if (!reviewRes.ok) {
    return submitFail(reviewRes, adId);
  }

  return { ok: true, adId };
}

/**
 * @param {{ adCreateData: object, geometry: object, forestWithSections: object, existingAdId?: number }} args
 */
export async function submitSellTimberAd({ adCreateData, geometry, forestWithSections, existingAdId }) {
  const forestPayload = buildForestWithSectionsPayload(forestWithSections);
  if (!forestPayload.sections.length) {
    return { ok: false, message: 'Lägg till minst en rad med virkesdata (steg 3).' };
  }

  const customArea = buildCustomAreaFromGeometry(geometry);
  if (!customArea) {
    return { ok: false, message: 'Välj eller rita områden på kartan (steg 2).' };
  }

  const typesRes = await fetchAdTypes();
  if (!typesRes.ok) {
    return submitFail(typesRes);
  }

  const typeId = resolveAdTypeId(typesRes.data, adCreateData?.selectedSale);
  if (!typeId) {
    return { ok: false, message: 'Ogiltig eller saknad annonstyp (steg 1).' };
  }

  const core = sellTimberCoreCreateFields(
    adCreateData,
    typeId,
    adCreateData?.selectedAction,
    (adCreateData?.comments ?? '').trim() || ''
  );
  if (!core.ok) {
    return core;
  }

  return submitSellTimberAdPipeline({
    validate: () => ({ ok: true }),
    createBody: core.createBody,
    buildFinalizePatch: () => ({
      forestWithSections: forestPayload,
      custom_area: customArea,
      crs: 4326,
      state: 20
    }),
    attachments: adCreateData?.attachments,
    existingAdId
  });
}

/**
 * Submit biobränsle (grot) Sell Timber flow — marker on map, grot_volume, required photo, no virkesdata.
 *
 * @param {{ adCreateData: object, geometry: object, existingAdId?: number }} args
 */
export async function submitSellTimberGrotAd({ adCreateData, geometry, existingAdId }) {
  const customArea = buildCustomAreaFromGeometry(geometry);
  const comments = grotCommentsForApi(adCreateData);
  const grot_volume = String(adCreateData?.grot_volume ?? '').trim();
  const grot_heap_covered = normalizeGrotJaNejForApi(adCreateData?.grot_heap_covered);
  const grot_collected = normalizeGrotJaNejForApi(adCreateData?.grot_collected);

  const typesRes = await fetchAdTypes();
  if (!typesRes.ok) {
    return submitFail(typesRes);
  }

  const typeId = resolveAdTypeId(typesRes.data, SELL_TIMBER_GROT_AD_TYPE);
  if (!typeId) {
    return { ok: false, message: 'Kunde inte hitta annonstyp för biobränsle (grot).' };
  }

  const core = sellTimberCoreCreateFields(adCreateData, typeId, GROT_ARRANGEMENT, comments ?? '');
  if (!core.ok) {
    return core;
  }

  const createBody = {
    ...core.createBody,
    grot_volume,
    grot_heap_covered,
    grot_collected
  };

  return submitSellTimberAdPipeline({
    validate: () => {
      const step3Missing = grotStep3ValidationMessages(adCreateData ?? {});
      if (step3Missing.length) {
        return {
          ok: false,
          message: `Komplettera steg 3 innan du slutför: ${step3Missing.join(', ')}.`
        };
      }
      if (!customArea) {
        return { ok: false, message: 'Placera minst en biobränsle-markör på kartan (steg 2).' };
      }
      if (!grotHasAtLeastOneAttachment(adCreateData?.attachments)) {
        return { ok: false, message: 'Ladda upp minst en bild på biobränslet.' };
      }
      return { ok: true };
    },
    createBody,
    buildFinalizePatch: () => buildGrotFinalizePatch(adCreateData, { customArea, comments }),
    attachments: adCreateData?.attachments,
    existingAdId
  });
}
